import * as React from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type { SiteRedirect } from '@gitbook/api-client';
import {
    Alert,
    Button,
    confirm,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogFooterSecondary,
    DialogHeader,
    Form,
    Heading,
    Submit,
    toast,
    useDialogAction,
    useDialogClose,
} from '@gitbook/spine-react';

import { apiClient, useSiteContext } from '../../../../../state';
import {
    SiteRedirectFieldset,
    type SiteRedirectFormOutputs,
    useSiteRedirectForm,
} from './SiteRedirectForm';

export function EditRedirectDialog(props: { siteRedirect: SiteRedirect }) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Edit redirect</Heading>
            </DialogHeader>
            <EditRedirectForm siteRedirect={props.siteRedirect} />
        </Dialog>
    );
}

function EditRedirectForm(props: { siteRedirect: SiteRedirect }) {
    const { organizationId, site } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();
    const closeEditDialog = useDialogClose();
    const [redirectError, setRedirectError] = React.useState<string | null>(null);
    const form = useSiteRedirectForm({
        disabled: isPending,
        defaultValues: {
            destination: props.siteRedirect.destination,
            source: props.siteRedirect.source,
        },
    });

    const onSubmit: SubmitHandler<SiteRedirectFormOutputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            try {
                await apiClient.siteRedirects.updateSiteRedirectById(
                    organizationId,
                    site.id,
                    props.siteRedirect.id,
                    {
                        destination: data.destination,
                        source: data.source,
                    }
                );
                toast.success('Redirect saved');
                await close();
            } catch (error) {
                if (error instanceof Error) {
                    setRedirectError(error.message);
                }
            }
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                {redirectError ? (
                    <Alert
                        className="mb-4"
                        size="small"
                        kind="danger"
                        title="Error while saving redirect"
                        details={redirectError}
                    />
                ) : null}
                <SiteRedirectFieldset />
            </DialogBody>
            <DialogFooter>
                <DialogFooterSecondary>
                    <Button
                        label="Delete redirect"
                        kind="danger"
                        onPress={async () => {
                            await confirm({
                                kind: 'danger',
                                title: 'Delete redirect',
                                description: 'Deleting redirect will remove it permanently.',
                                confirmLabel: 'Delete',
                                onConfirm: async () => {
                                    await apiClient.siteRedirects.deleteSiteRedirectById(
                                        organizationId,
                                        site.id,
                                        props.siteRedirect.id
                                    );
                                    toast.success('Redirect deleted');
                                    closeEditDialog();
                                },
                            });
                        }}
                    />
                </DialogFooterSecondary>
                <DialogCloseButton />
                <Submit label="Save" />
            </DialogFooter>
        </Form>
    );
}
